import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, Sparkles, QrCode, LineChart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/Logo";
import { api, formatErr, NICHES } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Shell = ({ title, subtitle, children }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-[#09090B] grain">
    <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-r border-zinc-800">
      <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-amber-500/10 blur-3xl" />
      <Logo size={44} />
      <div className="relative z-10 space-y-6 max-w-md">
        <h2 className="text-5xl font-extrabold leading-[1.05]">Seus achadinhos,<br /><span className="gold-text">com inteligência.</span></h2>
        <p className="text-zinc-400">Conteúdo com IA para cada rede, links rastreáveis com QR Code e métricas reais. Tudo dentro das regras da Shopee e das plataformas.</p>
        {[[Sparkles, "Gerador de conteúdo para 5 canais"], [QrCode, "Links com QR Code e cliques reais"], [LineChart, "Comissões, metas e campanhas"], [ShieldCheck, "Sem spam e sem métricas falsas"]].map(([I, t]) => (
          <div key={t} className="flex items-center gap-3 text-sm text-zinc-300"><I className="h-4 w-4 text-amber-400" />{t}</div>
        ))}
      </div>
      <div className="text-xs text-zinc-600">© RS Achadinhos IA</div>
    </div>
    <div className="flex items-center justify-center p-6 relative z-10">
      <div className="w-full max-w-sm rise">
        <div className="lg:hidden mb-10"><Logo /></div>
        <h1 className="text-3xl font-extrabold">{title}</h1>
        <p className="text-sm text-zinc-400 mt-1 mb-8">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);

const F = ({ id, label, ...p }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs text-zinc-400">{label}</Label>
    <Input id={id} data-testid={id} className="h-11 bg-zinc-900 border-zinc-800" required {...p} />
  </div>
);

const Submit = ({ busy, children, testid }) => (
  <Button type="submit" disabled={busy} className="btn-gold w-full h-11 rounded-full" data-testid={testid}>
    {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{children}
  </Button>
);

function useSubmit(fn) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await fn(); } catch (err) { setError(formatErr(err)); } finally { setBusy(false); }
  };
  return { busy, error, run };
}

const Err = ({ error }) => error ? <div className="text-sm text-rose-400" data-testid="auth-error-message">{error}</div> : null;

export function LoginPage() {
  const { setUser } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const { busy, error, run } = useSubmit(async () => { const { data } = await api.post("/auth/login", f); setUser(data); });
  return (
    <Shell title="Entrar" subtitle="Acesse seu painel de afiliado.">
      <form onSubmit={run} className="space-y-4">
        <F id="auth-login-email-input" label="E-mail" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <F id="auth-login-password-input" label="Senha" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <div className="text-right"><Link to="/recuperar-senha" className="text-xs text-amber-400 hover:underline" data-testid="auth-forgot-password-link">Esqueci minha senha</Link></div>
        <Err error={error} />
        <Submit busy={busy} testid="auth-login-submit-button">Entrar</Submit>
      </form>
      <p className="text-sm text-zinc-400 mt-6 text-center">Novo por aqui? <Link to="/cadastro" className="text-amber-400 font-semibold" data-testid="auth-register-link">Criar conta grátis</Link></p>
    </Shell>
  );
}

export function RegisterPage() {
  const { setUser } = useAuth();
  const [f, setF] = useState({ name: "", email: "", password: "", niche: "" });
  const { busy, error, run } = useSubmit(async () => { const { data } = await api.post("/auth/register", f); setUser(data); toast.success("Conta criada! Você ganhou 20 créditos de IA."); });
  return (
    <Shell title="Criar conta" subtitle="Comece grátis com 20 créditos de IA por mês.">
      <form onSubmit={run} className="space-y-4">
        <F id="auth-register-name-input" label="Nome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <F id="auth-register-email-input" label="E-mail" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <F id="auth-register-password-input" label="Senha (mín. 6 caracteres)" type="password" minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <div className="space-y-1.5">
          <Label className="text-xs text-zinc-400">Nicho principal</Label>
          <select className="field h-11" value={f.niche} onChange={(e) => setF({ ...f, niche: e.target.value })} data-testid="auth-register-niche-select">
            <option value="">Selecione (opcional)</option>{NICHES.map((n) => <option key={n}>{n}</option>)}
          </select>
        </div>
        <Err error={error} />
        <Submit busy={busy} testid="auth-register-submit-button">Criar conta</Submit>
      </form>
      <p className="text-sm text-zinc-400 mt-6 text-center">Já tem conta? <Link to="/entrar" className="text-amber-400 font-semibold" data-testid="auth-login-link">Entrar</Link></p>
    </Shell>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [res, setRes] = useState(null);
  const { busy, error, run } = useSubmit(async () => { const { data } = await api.post("/auth/forgot-password", { email }); setRes(data); });
  return (
    <Shell title="Recuperar senha" subtitle="Enviaremos um link para redefinir sua senha.">
      <form onSubmit={run} className="space-y-4">
        <F id="auth-forgot-email-input" label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Err error={error} />
        <Submit busy={busy} testid="auth-forgot-submit-button">Enviar link</Submit>
      </form>
      {res && (
        <div className="mt-6 card-lux p-4 text-sm space-y-2" data-testid="auth-forgot-result">
          <p className="text-zinc-300">{res.message}</p>
          {res.reset_link && <a href={res.reset_link} className="text-amber-400 break-all underline" data-testid="auth-reset-link">Abrir link de redefinição</a>}
        </div>
      )}
      <p className="text-sm text-zinc-400 mt-6 text-center"><Link to="/entrar" className="text-amber-400">Voltar ao login</Link></p>
    </Shell>
  );
}

export function ResetPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const { busy, error, run } = useSubmit(async () => {
    await api.post("/auth/reset-password", { token: params.get("token") || "", password: pwd });
    toast.success("Senha redefinida! Faça login.");
    nav("/entrar");
  });
  return (
    <Shell title="Nova senha" subtitle="Defina sua nova senha de acesso.">
      <form onSubmit={run} className="space-y-4">
        <F id="auth-reset-password-input" label="Nova senha" type="password" minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)} />
        <Err error={error} />
        <Submit busy={busy} testid="auth-reset-submit-button">Redefinir senha</Submit>
      </form>
    </Shell>
  );
}
